// per docs/shape-ops-medicine-label.md §2 §4
//
// The ONE place a medicine's identity becomes a string. It used to be three: the
// frontoffice picker's `formatMedicineLabel`, the bff's `stock/catalog-labels.ts`
// mirror of it, and an inline copy in `StockReportingPage.onPick` — so the chart
// legend, the stock tables, the chips and the draft row each had their own chance
// to disagree about what a medicine is called. `specs/021` refused to change the
// reading while that was true, and it was right to.
//
// It lives in `scope:shared` because `@nx/enforce-module-boundaries` lets
// `scope:backend` and `scope:frontoffice` depend on it and nothing else both sides
// can reach; both apps already import this package, so this is a new symbol on an
// existing edge.

export interface MedicineLabelParts {
  /** '' when the nomenclator has not named this code. Rare since spec 023, not impossible. */
  commercialName: string;
  dci: string;
  pharmaceuticalForm: string;
  dose: string;
  volume: string;
  division: string;
}

const SEPARATOR = ' · ';

/**
 * What a medicine is called, in one segment.
 *
 * The commercial name, falling back to the DCI. Until spec 023 the catalogue held
 * no brands and the DCI was the only answer; every one of the 6274 codes carries
 * its own now, and the wireframe's `MedicineIdentity` heading is `denumire`.
 */
export function medicineDisplayName(
  parts: Pick<MedicineLabelParts, 'commercialName' | 'dci'>,
): string {
  return parts.commercialName || parts.dci;
}

/**
 * The full identity, as the wireframe's `MedicineIdentity` reads it: the name
 * first, then the pharmaceutical facts, then the DCI in the catalogue group.
 *
 * `name === parts.dci ? '' : parts.dci` is one expression covering two cases that
 * would otherwise each need a branch: a code with no commercial name leads with
 * its DCI and must not then repeat it, and a generic marketed under its own INN
 * must not print the same word twice. A code with no brand therefore composes
 * exactly the string that shipped before this change.
 *
 * The DCI is kept rather than replaced. Caiet §Termeni defines *medicament unic*
 * by Formă, Doză, Volum, Divizare and DCI: the DCI is the identity and the brand
 * is descriptive, and three of the surfaces reading this string — the expiring
 * table, a submission's own rows, the stock table — are handed one opaque label by
 * the bff and have no second line to put it on.
 */
export function formatMedicineLabel(parts: MedicineLabelParts): string {
  const name = medicineDisplayName(parts);
  return [
    name,
    parts.pharmaceuticalForm,
    parts.dose,
    parts.volume,
    parts.division,
    name === parts.dci ? '' : parts.dci,
  ]
    .filter((part) => part.length > 0)
    .join(SEPARATOR);
}

/** The heading/metadata split the wireframe draws: the name, then everything else, small. */
export function splitMedicineLabel(label: string): { name: string; detail: string } {
  const [name, ...rest] = label.split(SEPARATOR);
  return { name: name ?? label, detail: rest.join(SEPARATOR) };
}
